import React, { useState, useEffect, useCallback, useRef } from 'react';
import Loader from '../../../components/Loader';
import Calling from '../../../api/Calling';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import generateCaptcha from '../../../components/captcha';

const AllChat = () => {
    const [loader, setLoader] = useState(false);
    const [chats, setChats] = useState([]);
    const [page, setPage] = useState(1);
    const [limit, setLimit] = useState(10);
    const [total, setTotal] = useState(0);
    const [activeTab, setActiveTab] = useState('unread'); // 'unread' or 'all'
    const [isRefreshing, setIsRefreshing] = useState(false);
    const [lastRefreshed, setLastRefreshed] = useState(new Date());
    const refreshTimerRef = useRef(null);
    const navigate = useNavigate();

    // New state for selected chats
    const [selectedChats, setSelectedChats] = useState([]);
    const [isDeleting, setIsDeleting] = useState(false);

    // Function to fetch data without showing the full page loader
    const fetchDataSilently = useCallback(async () => {
        try {
            setIsRefreshing(true);

            // Use status parameter to get appropriate chats
            const status = activeTab === 'unread' ? 'unread' : 'all';
            const resp = await Calling.getChatsByStatus(page, limit, status);

            if (resp.status) {
                // Check if we need to update the chats (compare with current state)
                setChats(prevChats => {
                    // If the data has changed (different length or IDs), update it
                    const hasChanged = prevChats.length !== resp.data.length ||
                        JSON.stringify(prevChats.map(c => c._id)) !== JSON.stringify(resp.data.map(c => c._id));

                    return hasChanged ? resp.data : prevChats;
                });

                setTotal(resp.total);
                setLastRefreshed(new Date());
            }
        } catch (error) {
            console.log('Silent refresh error:', error);
            // No toast on silent refresh errors to avoid disrupting the user
        } finally {
            setIsRefreshing(false);
        }
    }, [page, limit, activeTab]);

    // Function to fetch data with full loader (initial load and user-triggered actions)
    const fetchData = useCallback(async () => {
        try {
            setLoader(true);

            // Use status parameter to get appropriate chats
            const status = activeTab === 'unread' ? 'unread' : 'all';
            const resp = await Calling.getChatsByStatus(page, limit, status);

            if (resp.status) {
                setChats(resp.data);
                setTotal(resp.total);
                setLastRefreshed(new Date());
                // Clear selections when refreshing data
                setSelectedChats([]);
            } else {
                toast.error(resp.message);
            }
        } catch (error) {
            console.log(error);
            toast.error('Failed to fetch chats');
        } finally {
            setLoader(false);
        }
    }, [page, limit, activeTab]);

    // Setup background refresh
    useEffect(() => {
        // Initial fetch with loader
        fetchData();

        // Set up refresh interval for silent updates (every 20 seconds)
        refreshTimerRef.current = setInterval(() => {
            fetchDataSilently();
        }, 20000);

        // Clean up interval on component unmount
        return () => {
            if (refreshTimerRef.current) {
                clearInterval(refreshTimerRef.current);
            }
        };
    }, [fetchData, fetchDataSilently]);

    // Re-fetch with loader when page, limit, or activeTab changes
    useEffect(() => {
        fetchData();
    }, [page, limit, activeTab, fetchData]);

    // Function to handle pagination
    const handlePageChange = (newPage) => {
        if (newPage >= 1 && newPage <= Math.ceil(total / limit) && newPage !== page) {
            setPage(newPage);
        }
    };

    // Function to handle page size change
    const handleLimitChange = (newLimit) => {
        setLimit(newLimit);
        setPage(1); // Reset to first page when changing limit
    };

    // Function to handle tab change
    const handleTabChange = (tab) => {
        if (tab !== activeTab) {
            setActiveTab(tab);
            setPage(1); // Reset to first page when changing tabs
            setSelectedChats([]); // Clear selections when changing tabs
        }
    };

    // Function to handle manual refresh
    const handleManualRefresh = () => {
        fetchData();
    };

    // Function to handle chat click - navigate to chat page
    const handleChatClick = (chatId) => {
        // Simply navigate to the chat page
        // The getChatMessages API will automatically mark as read when admin views it
        navigate('/manage/contact/chat/read?id=' + chatId);
    };

    // New function to handle chat selection
    const handleChatSelect = (e, chatId) => {
        e.stopPropagation(); // Prevent navigation when clicking checkbox

        setSelectedChats(prev => {
            if (prev.includes(chatId)) {
                return prev.filter(id => id !== chatId);
            } else {
                return [...prev, chatId];
            }
        });
    };

    // New function to handle select all chats
    const handleSelectAll = () => {
        if (selectedChats.length === chats.length) {
            // If all are selected, deselect all
            setSelectedChats([]);
        } else {
            // Otherwise, select all
            setSelectedChats(chats.map(chat => chat._id));
        }
    };

    // New function to handle chat deletion
    const handleDeleteChats = async () => {
        if (selectedChats.length === 0) {
            toast.info('Please select at least one chat to delete');
            return;
        }
        const content = `Are you sure you want to delete ${selectedChats.length} ${selectedChats.length === 1 ? 'chat' : 'chats'}?`;
        const check = generateCaptcha(content);
        // Confirmation dialog
        if (!check) {
            return toast.error('Something went wrong');
        }
        try {
            setIsDeleting(true);
            console.log({ ids: selectedChats })
            const resp = await Calling.deleteChat({ ids: selectedChats });

            if (resp.status) {
                toast.success(`Successfully deleted ${selectedChats.length} ${selectedChats.length === 1 ? 'chat' : 'chats'}`);
                // Refresh data after deletion
                fetchData();
            } else {
                toast.error(resp.message || 'Failed to delete chats');
            }
        } catch (error) {
            console.log(error);
            toast.error('An error occurred while deleting chats');
        } finally {
            setIsDeleting(false);
        }
    };

    // Format the last refreshed time
    const formatLastRefreshed = () => {
        return lastRefreshed.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' });
    };

    if (loader && chats.length === 0) {
        return <Loader loader={loader} />;
    }

    // Calculate pagination values
    const totalPages = Math.ceil(total / limit);
    const startItem = total > 0 ? (page - 1) * limit + 1 : 0;
    const endItem = Math.min(page * limit, total);

    // Get unread count for badge
    const unreadCount = chats.filter(chat => !chat.readStatus.admin).length;

    return (
        <div className="px-3 sm:px-4 lg:ml-[4.4rem] w-full text-main-font bg-main-bg mt-16 md:mt-20">
            <div className="w-full mx-auto bg-white rounded-lg shadow-sm overflow-hidden">
                {/* Header */}
                <div className="px-6 py-4 bg-gradient-to-r from-main-blue to-primary-blue text-white flex justify-between items-center">
                    <div className="flex items-center">
                        <h2 className="text-xl font-bold">Support Chat Dashboard</h2>
                        <button
                            onClick={handleManualRefresh}
                            disabled={loader || isRefreshing || isDeleting}
                            className={`ml-3 p-1.5 rounded-full ${(loader || isRefreshing || isDeleting) ? 'bg-white/10 cursor-not-allowed' : 'bg-white/20 hover:bg-white/30'} transition-colors`}
                            title="Refresh chats"
                        >
                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                className={`h-5 w-5 ${isRefreshing ? 'animate-spin' : ''}`}
                                fill="none"
                                viewBox="0 0 24 24"
                                stroke="currentColor"
                            >
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                            </svg>
                        </button>
                        <span className="ml-3 text-xs text-white/70">Last updated: {formatLastRefreshed()}</span>
                    </div>
                    <div className="flex items-center space-x-2">
                        <span className="text-sm">Show:</span>
                        <select
                            value={limit}
                            onChange={(e) => handleLimitChange(Number(e.target.value))}
                            className="border bg-white text-main-blue rounded px-2 py-1 text-sm"
                        >
                            <option value={5}>5</option>
                            <option value={10}>10</option>
                            <option value={20}>20</option>
                            <option value={50}>50</option>
                        </select>
                    </div>
                </div>

                {/* Tabs */}
                <div className="border-b border-main-border">
                    <div className="flex">
                        <button
                            onClick={() => handleTabChange('unread')}
                            className={`px-6 py-3 text-sm font-medium relative ${activeTab === 'unread'
                                ? 'text-main-blue border-b-2 border-main-blue'
                                : 'text-main-subfont hover:text-main-blue'
                                }`}
                        >
                            Unread Chats
                            {unreadCount > 0 && activeTab !== 'unread' && (
                                <span className="absolute top-2 right-1 bg-main-delete text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
                                    {unreadCount}
                                </span>
                            )}
                        </button>
                        <button
                            onClick={() => handleTabChange('all')}
                            className={`px-6 py-3 text-sm font-medium ${activeTab === 'all'
                                ? 'text-main-blue border-b-2 border-main-blue'
                                : 'text-main-subfont hover:text-main-blue'
                                }`}
                        >
                            All Chats
                        </button>
                    </div>
                </div>

                {/* Chat List */}
                <div className="p-4 relative">
                    {/* Action bar - only visible when chats are selected */}
                    {chats.length > 0 && (
                        <div className="flex justify-between items-center mb-4 bg-main-bg/50 p-3 rounded-lg">
                            <div className="flex items-center">
                                <div className="flex items-center mr-4">
                                    <input
                                        type="checkbox"
                                        id="selectAll"
                                        checked={chats.length > 0 && selectedChats.length === chats.length}
                                        onChange={handleSelectAll}
                                        className="w-4 h-4 text-main-blue rounded border-main-border focus:ring-main-blue"
                                    />
                                    <label htmlFor="selectAll" className="ml-2 text-sm text-main-subfont">
                                        {selectedChats.length === 0 ? 'Select All' :
                                            selectedChats.length === chats.length ? 'Deselect All' :
                                                `Selected ${selectedChats.length}`}
                                    </label>
                                </div>
                                {selectedChats.length > 0 && (
                                    <button
                                        onClick={handleDeleteChats}
                                        disabled={isDeleting || loader}
                                        className={`flex items-center px-3 py-1.5 rounded text-sm text-white bg-main-delete hover:bg-opacity-90 transition-colors ${(isDeleting || loader) ? 'opacity-60 cursor-not-allowed' : ''}`}
                                    >
                                        {isDeleting ? (
                                            <>
                                                <svg className="animate-spin h-4 w-4 mr-1.5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                                </svg>
                                                Deleting...
                                            </>
                                        ) : (
                                            <>
                                                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                                </svg>
                                                Delete Selected
                                            </>
                                        )}
                                    </button>
                                )}
                            </div>

                            {/* Semi-transparent overlay during silent refreshes */}
                            {isRefreshing && chats.length > 0 && (
                                <div className="bg-white/80 rounded-full px-2 py-1 z-10 flex items-center text-main-subfont text-xs shadow-sm">
                                    <svg className="animate-spin h-3 w-3 mr-1" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                    </svg>
                                    Refreshing...
                                </div>
                            )}
                        </div>
                    )}

                    {chats.length === 0 && !loader ? (
                        <div className="text-center p-8 bg-main-bg rounded-md">
                            <svg className="w-12 h-12 mx-auto text-main-subfont" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                            </svg>
                            <p className="mt-2 text-main-subfont">No {activeTab === 'unread' ? 'unread' : ''} chats available</p>
                        </div>
                    ) : (
                        <div className="space-y-3 transition-all duration-300">
                            {chats.map((chat) => (
                                <div
                                    key={chat._id}
                                    className={`p-4 border rounded-lg transition-all duration-200 cursor-pointer ${selectedChats.includes(chat.userId)
                                        ? 'bg-main-blue/10 border-main-blue'
                                        : !chat.readStatus.admin
                                            ? 'bg-main-th border-main-blue/20 hover:shadow-md'
                                            : 'bg-white hover:bg-main-thHover border-main-border'
                                        }`}
                                    onClick={() => handleChatClick(chat.userId)}
                                >
                                    <div className="flex items-center space-x-3">
                                        <div className="flex items-center" onClick={(e) => e.stopPropagation()}>
                                            <input
                                                type="checkbox"
                                                checked={selectedChats.includes(chat._id)}
                                                onChange={(e) => handleChatSelect(e, chat._id)}
                                                className="w-4 h-4 text-main-blue rounded border-main-border focus:ring-main-blue mr-2"
                                            />
                                        </div>
                                        {chat.userInfo.u_profile ? (
                                            <img
                                                src={chat.userInfo.u_profile}
                                                alt={chat.userInfo.u_name}
                                                className="w-12 h-12 rounded-full object-cover border-2 border-main-border"
                                            />
                                        ) : (
                                            <div className="w-12 h-12 rounded-full bg-gradient-to-r from-main-blue to-primary-blue flex items-center justify-center text-white text-lg font-medium">
                                                {chat.userInfo.u_name.charAt(0)}
                                            </div>
                                        )}
                                        <div className="flex-1 min-w-0">
                                            <div className="flex justify-between items-start">
                                                <h3 className="font-semibold text-main-font truncate">{chat.userInfo.u_name}</h3>
                                                <div className="flex items-center">
                                                    {!chat.readStatus.admin && (
                                                        <span className="inline-block w-2 h-2 bg-main-blue rounded-full mr-2"></span>
                                                    )}
                                                    <span className="text-xs text-main-subfont whitespace-nowrap">
                                                        {new Date(chat.lastMessage.timestamp).toLocaleTimeString([], {
                                                            hour: '2-digit',
                                                            minute: '2-digit'
                                                        })}
                                                    </span>
                                                </div>
                                            </div>
                                            <p className="text-sm text-main-subfont truncate mt-1">
                                                {chat.lastMessage.text || (chat.lastMessage.image ? 'Sent an image' : 'No message')}
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}

                    {/* Pagination */}
                    {total > 0 && (
                        <div className="mt-6 border-t border-main-border pt-4">
                            <div className="flex flex-col sm:flex-row justify-between items-center gap-4">
                                <div className="text-sm text-main-subfont order-2 sm:order-1">
                                    Showing {startItem} to {endItem} of {total} items
                                </div>

                                <div className="flex items-center space-x-1 order-1 sm:order-2">
                                    <button
                                        onClick={() => handlePageChange(1)}
                                        disabled={page === 1 || loader}
                                        className={`px-2 py-1 rounded text-sm ${page === 1 || loader
                                            ? 'bg-main-bg text-main-subfont cursor-not-allowed'
                                            : 'bg-main-bg hover:bg-main-thHover text-main-font'
                                            }`}
                                    >
                                        First
                                    </button>
                                    <button
                                        onClick={() => handlePageChange(page - 1)}
                                        disabled={page === 1 || loader}
                                        className={`px-2 py-1 rounded text-sm ${page === 1 || loader
                                            ? 'bg-main-bg text-main-subfont cursor-not-allowed'
                                            : 'bg-main-bg hover:bg-main-thHover text-main-font'
                                            }`}
                                    >
                                        Prev
                                    </button>

                                    {/* Dynamically generate page buttons with ellipsis for large page counts */}
                                    {Array.from({ length: totalPages }, (_, i) => i + 1)
                                        .filter(p => {
                                            // Show first and last page
                                            if (p === 1 || p === totalPages) return true;
                                            // Show pages around current page
                                            if (p >= page - 1 && p <= page + 1) return true;
                                            return false;
                                        })
                                        .map((p, i, arr) => {
                                            // Add ellipsis
                                            if (i > 0 && arr[i - 1] !== p - 1) {
                                                return (
                                                    <React.Fragment key={`ellipsis-${p}`}>
                                                        <span className="px-2 py-1 text-main-subfont">...</span>
                                                        <button
                                                            onClick={() => handlePageChange(p)}
                                                            disabled={loader}
                                                            className={`w-8 h-8 rounded-full text-sm ${page === p
                                                                ? 'bg-main-blue text-white'
                                                                : 'bg-main-bg hover:bg-main-thHover text-main-font'
                                                                }`}
                                                        >
                                                            {p}
                                                        </button>
                                                    </React.Fragment>
                                                );
                                            }
                                            return (
                                                <button
                                                    key={p}
                                                    onClick={() => handlePageChange(p)}
                                                    disabled={loader}
                                                    className={`w-8 h-8 rounded-full text-sm ${page === p
                                                        ? 'bg-main-blue text-white'
                                                        : 'bg-main-bg hover:bg-main-thHover text-main-font'
                                                        }`}
                                                >
                                                    {p}
                                                </button>
                                            );
                                        })}

                                    <button
                                        onClick={() => handlePageChange(page + 1)}
                                        disabled={page === totalPages || loader}
                                        className={`px-2 py-1 rounded text-sm ${page === totalPages || loader
                                            ? 'bg-main-bg text-main-subfont cursor-not-allowed'
                                            : 'bg-main-bg hover:bg-main-thHover text-main-font'
                                            }`}
                                    >
                                        Next
                                    </button>
                                    <button
                                        onClick={() => handlePageChange(totalPages)}
                                        disabled={page === totalPages || loader}
                                        className={`px-2 py-1 rounded text-sm ${page === totalPages || loader
                                            ? 'bg-main-bg text-main-subfont cursor-not-allowed'
                                            : 'bg-main-bg hover:bg-main-thHover text-main-font'
                                            }`}
                                    >
                                        Last
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {/* Loading indicator */}
            {(loader && chats.length > 0) || isDeleting ? (
                <Loader loader={true} />
            ) : null}
        </div>
    );
};

export default AllChat;